import { Fragment, useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Icon } from '@/components/ui/Icon';
import { MultiSelectDropdown } from '@/components/ui/MultiSelectDropdown';
import { RetryImage } from '@/components/ui/RetryImage';
import { DatePickerSingle } from '@/components/ui/DatePickerSingle';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { useTimezone } from '@/contexts/TimezoneContext';
import {
  useBlogPost,
  usePublishBlogPost,
  useUpdateBlogOnWp,
  useSyncBlogStatus,
  useUploadBlogFeaturedImage,
} from '@/hooks/useBlog';
import {
  useWordPressChannels,
  useWordPressCategories,
  useWordPressTags,
  useCreateWordPressCategory,
  useCreateWordPressTag,
} from '@/hooks/useWordPress';
import { WordPressCategory, WordPressTag } from '@/services/wordpress.api';
import { PublishBlogAction, BlogPostStatus, BlogPostImage } from '@/services/blog.api';
import { sanitizeHtml, hideFigureByImageIndex } from '@/utils/sanitizeHtml';
import { formatApiError, isGlobalGatingHandledError } from '@/services/api';
import { toast } from '@/utils/toast';
import { BlogGenerateSocialModal } from '@/components/blog/BlogGenerateSocialModal';
import { BlogFeaturedImageLibraryModal } from '@/components/blog/BlogFeaturedImageLibraryModal';
import { SiteFavicon } from '@/components/blog/SiteFavicon';
import { BlogQuotaUsageLine } from '@/components/blog/BlogQuotaUsageLine';
import { useInvalidateQuotaUsage } from '@/hooks/useQuota';

/**
 * Status pill recipe shared with the (separately redesigned) Blog list/new
 * pages for visual cohesion — kept LOCAL to this file per design brief
 * (no new shared component in components/blog/).
 */
const STATUS_PILL_CLASSES: Record<BlogPostStatus, string> = {
  DRAFT: 'bg-amber-100 text-amber-700',
  SCHEDULED: 'bg-soft-mint text-dark-teal',
  PUBLISHED: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
  // WP post was trashed/deleted directly on WordPress — muted "no longer
  // live" treatment, distinct from FAILED (our publish attempt failing).
  TRASHED: 'bg-gray-100 text-gray-500',
};

const STATUS_ICON: Record<BlogPostStatus, string> = {
  DRAFT: ICONS.DRAFT,
  SCHEDULED: ICONS.CLOCK,
  PUBLISHED: ICONS.CHECK_CIRCLE,
  FAILED: ICONS.WARNING,
  TRASHED: ICONS.RECYCLE_BIN,
};

interface StatusPillProps {
  status: BlogPostStatus;
  label: string;
}

const StatusPill = ({ status, label }: StatusPillProps) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0',
      STATUS_PILL_CLASSES[status]
    )}
  >
    <Icon name={STATUS_ICON[status]} size="xs" ariaHidden />
    {label}
  </span>
);

/** Reusable small meta chip — matches PostTable's `bg-gray-100 text-gray-600` convention. */
const MetaBadge = ({ icon, leading, children }: { icon?: string; leading?: React.ReactNode; children: React.ReactNode }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded">
    {leading ? leading : icon && <Icon name={icon} size="xs" ariaHidden />}
    {children}
  </span>
);

/** Minimal shape needed to resolve a term id → its display name (covers both WordPressCategory and WordPressTag). */
interface NamedTerm {
  id: number;
  name: string;
}

/**
 * Labeled row of resolved category/tag pills for the LOCKED summary card —
 * shows nothing when there are no applied ids, a skeleton row while the
 * name list is still loading, and falls back to `#<id>` if a name can't be
 * resolved (e.g. term deleted on WordPress after the post was published).
 */
interface TermClusterProps {
  label: string;
  icon: string;
  ids: number[];
  terms: NamedTerm[] | undefined;
  isLoading: boolean;
  variant?: 'gray' | 'mint';
}

const TermCluster = ({ label, icon, ids, terms, isLoading, variant = 'gray' }: TermClusterProps) => {
  if (ids.length === 0) return null;

  const pillClasses =
    variant === 'mint'
      ? 'inline-flex items-center gap-1 px-2 py-0.5 bg-soft-mint text-dark-teal text-xs font-medium rounded-full'
      : 'inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded';

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs font-medium text-gray-400">{label}</span>
      {isLoading && !terms
        ? ids.map((id) => (
            <span key={id} className="inline-block w-14 h-5 bg-gray-100 rounded animate-pulse" aria-hidden="true" />
          ))
        : ids.map((id) => {
            const name = terms?.find((term) => term.id === id)?.name ?? `#${id}`;
            return (
              <span key={id} className={pillClasses}>
                <Icon name={icon} size="xs" ariaHidden />
                {name}
              </span>
            );
          })}
    </div>
  );
};

/**
 * Small "create a new term that doesn't exist yet" affordance, sat beside a
 * categories/tags `MultiSelectDropdown` (Phase 3, decision F). Non-invasive
 * by design — it never touches `MultiSelectDropdown` itself, it just calls
 * the passed-in create-mutation and hands the resulting `{id,name}` back to
 * the caller to fold into the current selection. WordPress's `term_exists`
 * case is transparent here — the backend already resolves it and returns
 * the existing term's id/name, so this component just selects it.
 */
interface InlineCreateTermProps {
  buttonLabel: string;
  inputPlaceholder: string;
  onCreate: (name: string) => Promise<{ id: number; name: string }>;
  onCreated: (term: { id: number; name: string }) => void;
}

const InlineCreateTerm = ({ buttonLabel, inputPlaceholder, onCreate, onCreated }: InlineCreateTermProps) => {
  const { t } = useTranslation('common');
  const [isAdding, setIsAdding] = useState(false);
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const cancel = () => {
    setIsAdding(false);
    setName('');
    setErrorMsg(null);
  };

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || isSubmitting) return;
    setIsSubmitting(true);
    setErrorMsg(null);
    try {
      const term = await onCreate(trimmed);
      onCreated(term);
      cancel();
    } catch (err) {
      setErrorMsg(formatApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAdding) {
    return (
      <button
        type="button"
        onClick={() => setIsAdding(true)}
        className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 flex-shrink-0"
      >
        <Icon name={ICONS.PLUS_CIRCLE} size="xs" ariaHidden />
        {buttonLabel}
      </button>
    );
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-1.5">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            } else if (e.key === 'Escape') {
              cancel();
            }
          }}
          placeholder={inputPlaceholder}
          maxLength={200}
          autoFocus
          disabled={isSubmitting}
          className="w-32 sm:w-40 px-2 py-1 text-xs border border-gray-200 rounded-md text-gray-700 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-colors disabled:bg-gray-50"
        />
        <button
          type="button"
          onClick={submit}
          disabled={!name.trim() || isSubmitting}
          className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-primary-100 text-primary-700 hover:bg-primary-200 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          aria-label={t('blog_term_add', { defaultValue: 'Add' })}
        >
          {isSubmitting ? (
            <span className="w-3 h-3 rounded-full border-2 border-primary-400 border-t-transparent animate-spin" />
          ) : (
            <Icon name={ICONS.CHECK} size="xs" ariaHidden />
          )}
        </button>
        <button
          type="button"
          onClick={cancel}
          className="inline-flex items-center justify-center w-6 h-6 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex-shrink-0"
          aria-label={t('blog_term_cancel', { defaultValue: 'Cancel' })}
        >
          <Icon name={ICONS.CROSS} size="xs" ariaHidden />
        </button>
      </div>
      {errorMsg && <p className="text-xs text-red-600 text-right max-w-[200px]">{errorMsg}</p>}
    </div>
  );
};

/**
 * Categories + tags picker pair, shared between the pre-publish panel and
 * the Phase 3 "Update on WordPress" panel — both edit the SAME
 * `categoryIds`/`tagIds` state on the parent, so switching between the two
 * panels never loses a selection. Bundles the inline create-term
 * affordance beside each label (Phase 3, decision F).
 */
interface CategoryTagFieldsProps {
  channelId: number;
  categories: WordPressCategory[] | undefined;
  tags: WordPressTag[] | undefined;
  isLoadingCategories: boolean;
  isLoadingTags: boolean;
  categoryIds: number[];
  tagIds: number[];
  onCategoryIdsChange: (ids: number[]) => void;
  onTagIdsChange: (ids: number[]) => void;
}

const CategoryTagFields = ({
  channelId,
  categories,
  tags,
  isLoadingCategories,
  isLoadingTags,
  categoryIds,
  tagIds,
  onCategoryIdsChange,
  onTagIdsChange,
}: CategoryTagFieldsProps) => {
  const { t } = useTranslation('common');
  const createCategoryMutation = useCreateWordPressCategory(channelId);
  const createTagMutation = useCreateWordPressTag(channelId);

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
            {t('blog_categories_label', { defaultValue: 'Categories' })}
          </label>
          <InlineCreateTerm
            buttonLabel={t('blog_new_category', { defaultValue: 'New category' })}
            inputPlaceholder={t('blog_new_category_placeholder', { defaultValue: 'Category name' })}
            onCreate={(name) => createCategoryMutation.mutateAsync(name)}
            onCreated={(term) => {
              if (!categoryIds.includes(term.id)) onCategoryIdsChange([...categoryIds, term.id]);
            }}
          />
        </div>
        <MultiSelectDropdown
          value={categoryIds}
          onChange={onCategoryIdsChange}
          isLoading={isLoadingCategories}
          options={(categories ?? []).map((c) => ({ id: c.id, name: c.name, subtitle: `${c.count} posts` }))}
          placeholder={t('blog_categories_placeholder', { defaultValue: 'Select categories' })}
        />
      </div>
      <div>
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
            {t('blog_tags_label', { defaultValue: 'Tags' })}
          </label>
          <InlineCreateTerm
            buttonLabel={t('blog_new_tag', { defaultValue: 'New tag' })}
            inputPlaceholder={t('blog_new_tag_placeholder', { defaultValue: 'Tag name' })}
            onCreate={(name) => createTagMutation.mutateAsync(name)}
            onCreated={(term) => {
              if (!tagIds.includes(term.id)) onTagIdsChange([...tagIds, term.id]);
            }}
          />
        </div>
        <MultiSelectDropdown
          value={tagIds}
          onChange={onTagIdsChange}
          isLoading={isLoadingTags}
          options={(tags ?? []).map((tg) => ({ id: tg.id, name: tg.name, subtitle: `${tg.count} posts` }))}
          placeholder={t('blog_tags_placeholder', { defaultValue: 'Select tags' })}
        />
      </div>
    </div>
  );
};

/**
 * Featured-image thumbnail strip — shared between the pre-publish panel and
 * the Phase 3 "Update on WordPress" panel. Beyond the Doc-pulled strip, lets
 * the user pick from the team's media library (`BlogFeaturedImageLibraryModal`)
 * or upload a brand-new file (`useUploadBlogFeaturedImage`) — either of which
 * may select an image that isn't part of `images` at all, so the id AND its
 * preview url are always reported together via `onSelect`.
 */
interface FeaturedImagePickerProps {
  images: BlogPostImage[];
  selectedMediaId: number | undefined;
  /** Preview url for the current `selectedMediaId` when it did NOT come from
   *  `images` (a library pick or a fresh upload) — lets the picker still
   *  render a selected thumbnail for it. */
  selectedPreviewUrl: string | undefined;
  onSelect: (mediaId: number, url?: string) => void;
}

const FeaturedImagePicker = ({ images, selectedMediaId, selectedPreviewUrl, onSelect }: FeaturedImagePickerProps) => {
  const { t } = useTranslation('common');
  const [libraryModalOpen, setLibraryModalOpen] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadMutation = useUploadBlogFeaturedImage();

  const isSelectedFromStrip = images.some((img) => img.mediaId === selectedMediaId);
  const showExternalSelection = selectedMediaId != null && !isSelectedFromStrip && !!selectedPreviewUrl;

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset immediately so picking the exact same file twice still fires `onChange`.
    e.target.value = '';
    if (!file) return;

    setUploadError(null);
    try {
      const uploaded = await uploadMutation.mutateAsync(file);
      onSelect(uploaded.id, uploaded.url);
    } catch (err) {
      const msg = formatApiError(err).message;
      setUploadError(msg || t('blog_featured_upload_error', { defaultValue: 'Could not upload that image. Please try again.' }));
    }
  };

  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
        {t('blog_featured_image_label', { defaultValue: 'Featured Image' })}
      </label>
      {(images.length > 0 || showExternalSelection) && (
        <div className="flex flex-wrap gap-2 mb-2.5">
          {showExternalSelection && (
            <div className="relative w-16 h-16 rounded-lg overflow-hidden border-2 border-primary-500">
              <RetryImage src={selectedPreviewUrl} alt="" className="w-full h-full object-cover" />
              <span className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-primary-500 flex items-center justify-center">
                <Icon name={ICONS.CHECK} size="xs" color="text-white" ariaHidden />
              </span>
            </div>
          )}
          {images.map((img) => (
            <button
              key={img.id}
              type="button"
              onClick={() => onSelect(img.mediaId, img.media.url)}
              className={cn(
                'relative w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors',
                selectedMediaId === img.mediaId ? 'border-primary-500' : 'border-transparent hover:border-gray-300'
              )}
            >
              <RetryImage src={img.media.url} alt="" className="w-full h-full object-cover" />
              {selectedMediaId === img.mediaId && (
                <span className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-primary-500 flex items-center justify-center">
                  <Icon name={ICONS.CHECK} size="xs" color="text-white" ariaHidden />
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setLibraryModalOpen(true)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <Icon name={ICONS.FOLDER} size="xs" ariaHidden />
          {t('blog_featured_from_library', { defaultValue: 'From library' })}
        </button>
        <button
          type="button"
          onClick={handleUploadClick}
          disabled={uploadMutation.isPending}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-60"
        >
          {uploadMutation.isPending ? (
            <Icon name={ICONS.SPINNER} size="xs" ariaHidden className="animate-spin" />
          ) : (
            <Icon name={ICONS.UPLOAD} size="xs" ariaHidden />
          )}
          {t('blog_featured_upload', { defaultValue: 'Upload' })}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
      {uploadError && (
        <p className="text-xs text-red-600 mt-1.5" role="alert">
          {uploadError}
        </p>
      )}

      <BlogFeaturedImageLibraryModal
        isOpen={libraryModalOpen}
        onClose={() => setLibraryModalOpen(false)}
        selectedMediaId={selectedMediaId}
        onSelect={(img) => onSelect(img.id, img.url)}
      />
    </div>
  );
};

/**
 * Hand-typeset "reading experience" classes for the sanitized blog HTML.
 * Scoped to this file (no Tailwind Typography plugin installed) — mirrors
 * the `[&_h1]:...` arbitrary-variant convention the previous inline preview
 * already used, just considerably more editorial.
 */
const ARTICLE_CLASSES = [
  '[&>*:first-child]:mt-0',
  '[&_h1]:font-wagner [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:text-gray-900 [&_h1]:mt-10 [&_h1]:mb-4 [&_h1]:leading-snug',
  '[&_h2]:font-wagner [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:mt-9 [&_h2]:mb-3 [&_h2]:leading-snug',
  '[&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-gray-900 [&_h3]:mt-7 [&_h3]:mb-2',
  '[&_p]:my-4 [&_p]:text-[1.0625rem] [&_p]:leading-[1.8] [&_p]:text-gray-700',
  '[&_a]:text-primary-600 [&_a]:underline [&_a]:decoration-primary-300 [&_a]:decoration-2 [&_a]:underline-offset-2 [&_a:hover]:text-primary-700',
  '[&_strong]:font-semibold [&_strong]:text-gray-900 [&_em]:italic',
  '[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-4 [&_ul]:space-y-2 [&_ul]:text-gray-700',
  '[&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-4 [&_ol]:space-y-2 [&_ol]:text-gray-700',
  '[&_li]:leading-[1.8]',
  '[&_blockquote]:border-l-4 [&_blockquote]:border-soft-mint [&_blockquote]:pl-5 [&_blockquote]:py-1 [&_blockquote]:my-6 [&_blockquote]:italic [&_blockquote]:text-gray-600',
  '[&_figure]:my-8 [&_figure]:mx-0',
  '[&_img]:rounded-xl [&_img]:shadow-sm [&_img]:max-w-full [&_img]:h-auto [&_img]:mx-auto [&_img]:block',
  '[&_figcaption]:text-center [&_figcaption]:text-xs [&_figcaption]:text-gray-400 [&_figcaption]:mt-2.5',
  '[&_hr]:my-10 [&_hr]:border-gray-200',
  '[&_table]:w-full [&_table]:my-6 [&_table]:text-sm [&_table]:border-collapse',
  '[&_th]:border [&_th]:border-gray-200 [&_th]:bg-gray-50 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:text-gray-700',
  '[&_td]:border [&_td]:border-gray-200 [&_td]:px-3 [&_td]:py-2 [&_td]:text-gray-700',
].join(' ');

const ACTION_SEGMENTS: { value: PublishBlogAction; icon: string }[] = [
  { value: 'publish', icon: ICONS.ROCKET },
  { value: 'draft', icon: ICONS.DRAFT },
  { value: 'schedule', icon: ICONS.CLOCK },
];

/**
 * Blog preview/confirm screen (SPEC.md Phase 1, design point #1 — the core
 * of this phase). Used both immediately after a fresh Google Doc pull and
 * when revisiting a DRAFT/FAILED blog post to finish publishing it.
 *
 * Once a blog post has a live `wpPostId` (PUBLISHED or SCHEDULED — WordPress
 * assigns the post id immediately even for scheduled/`future` posts), v1 has
 * no "update existing" capability (decision B, Phase 3 follow-up) — the
 * form becomes a read-only summary with a link to the live WordPress post.
 *
 * Layout: an editorial two-column "preview + publish" surface — a real
 * reading experience on the left, a sticky publish panel on the right.
 */
export const BlogDetail = () => {
  const { id } = useParams<{ id: string }>();
  const blogPostId = id ? parseInt(id, 10) : undefined;
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { formatDateTime, formatRelativeDate, toUTC } = useTimezone();

  const { data: post, isLoading, error } = useBlogPost(blogPostId);
  const { data: wpChannels, isLoading: isLoadingChannels } = useWordPressChannels();

  const STATUS_LABEL: Record<BlogPostStatus, string> = {
    DRAFT: t('blog_status_draft', { defaultValue: 'Draft' }),
    SCHEDULED: t('blog_status_scheduled', { defaultValue: 'Scheduled' }),
    PUBLISHED: t('blog_status_published', { defaultValue: 'Published' }),
    FAILED: t('blog_status_failed', { defaultValue: 'Failed' }),
    TRASHED:
      post?.wpStatus === 'deleted'
        ? t('blog_status_removed', { defaultValue: 'Removed' })
        : t('blog_status_trashed', { defaultValue: 'Trashed' }),
  };
  const ACTION_LABEL: Record<PublishBlogAction, string> = {
    publish: t('blog_action_publish_short', { defaultValue: 'Publish' }),
    draft: t('blog_action_draft_short', { defaultValue: 'Draft' }),
    schedule: t('blog_action_schedule_short', { defaultValue: 'Schedule' }),
  };
  const publishMutation = usePublishBlogPost(blogPostId ?? 0);
  const invalidateQuotaUsage = useInvalidateQuotaUsage();
  const updateOnWpMutation = useUpdateBlogOnWp(blogPostId ?? 0);
  const syncStatusMutation = useSyncBlogStatus(blogPostId ?? 0);

  // ─── Form state ─────────────────────────────────────────────────────────
  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [slug, setSlug] = useState('');
  const [channelId, setChannelId] = useState<number | undefined>(undefined);
  const [featuredMediaId, setFeaturedMediaId] = useState<number | undefined>(undefined);
  // Preview url paired with `featuredMediaId` — needed whenever the selected
  // featured image isn't part of `post.images` (a library pick or a fresh
  // upload), so the hero/thumbnail can still render it. See `heroImageUrl`.
  const [featuredPreviewUrl, setFeaturedPreviewUrl] = useState<string | undefined>(undefined);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [action, setAction] = useState<PublishBlogAction>('publish');
  const [scheduledAtLocal, setScheduledAtLocal] = useState('');
  const [scheduleTouched, setScheduleTouched] = useState(false);
  const [generateSocialModalOpen, setGenerateSocialModalOpen] = useState(false);

  // Phase 3 — "Edit & Update" panel for an already-published/scheduled blog.
  // Reuses the exact same title/excerpt/slug/featuredMediaId/categoryIds/
  // tagIds state above (already hydrated from the live post below) so the
  // pre-publish panel and this panel never drift out of sync.
  const [isEditingLocked, setIsEditingLocked] = useState(false);
  const [refreshFromDoc, setRefreshFromDoc] = useState(false);
  const [updateErrorMsg, setUpdateErrorMsg] = useState<string | null>(null);

  // Hydrate form once the post loads (only once, so in-progress edits aren't clobbered by refetches).
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (!post || hydrated) return;
    setTitle(post.title);
    setExcerpt(post.excerpt ?? '');
    setSlug(post.slug ?? '');
    setChannelId(post.channelId ?? undefined);
    const initialFeaturedMediaId = post.featuredMediaId ?? post.images.find((i) => i.isFeatured)?.mediaId ?? undefined;
    setFeaturedMediaId(initialFeaturedMediaId);
    const initialFeaturedImage = post.images.find((i) => i.mediaId === initialFeaturedMediaId);
    setFeaturedPreviewUrl(initialFeaturedImage?.media.url ?? post.featuredMedia?.url ?? undefined);
    setCategoryIds(post.categoryIds ?? []);
    setTagIds(post.tagIds ?? []);
    setHydrated(true);
  }, [post, hydrated]);

  // Preselect the only WordPress site once channels load, if the post doesn't already have one.
  useEffect(() => {
    if (channelId !== undefined) return;
    if (wpChannels && wpChannels.length === 1) {
      setChannelId(wpChannels[0].id);
    }
  }, [wpChannels, channelId]);

  const { data: categories, isLoading: isLoadingCategories } = useWordPressCategories(channelId);
  const { data: tags, isLoading: isLoadingTags } = useWordPressTags(channelId);

  // WordPress category/tag term IDs are per-site — switching to a different
  // connected site makes the current selections meaningless, so reset them.
  // (Only when actually changing an existing selection; the initial pick
  // from `undefined` — via hydration or auto-preselect — must not clear
  // the selections it just set.)
  const handleChannelChange = (value: string) => {
    const next = value ? parseInt(value, 10) : undefined;
    if (channelId !== undefined && next !== channelId) {
      setCategoryIds([]);
      setTagIds([]);
    }
    setChannelId(next);
  };

  const isLocked = post ? (post.status === 'PUBLISHED' || post.status === 'SCHEDULED') && !!post.wpPostUrl : false;

  // Doc index of the currently-selected featured image (== its `order` in
  // `post.images`) — `undefined` when the selection is a library/uploaded
  // image not among the pulled Doc images, in which case nothing is hidden
  // below (correctly so: that image was never in `htmlBody` to begin with).
  // Falls back to the post's own featured image while `featuredMediaId` is
  // still unhydrated (hydration effect above hasn't run yet), so the correct
  // figure is hidden on the very first render instead of flashing duplicated
  // for a frame (cdb #301 code-review Low finding). Once the user picks a
  // featured image, local `featuredMediaId` takes precedence again.
  const effectiveFeaturedMediaId =
    featuredMediaId ?? post?.featuredMediaId ?? post?.images.find((i) => i.isFeatured)?.mediaId;
  const featuredDocIndex = post?.images.find((i) => i.mediaId === effectiveFeaturedMediaId)?.order;

  // Backend now emits the FULL body (all Doc images) in `htmlBody`, each
  // figure tagged `data-aa-image-index`. Hide whichever figure matches the
  // currently-selected featured/hero image so it isn't shown twice — once
  // as the hero, once inline in the body — and keep it reactive to the
  // user changing the featured image (cdb #301 follow-up).
  const sanitizedPreview = useMemo(
    () => (post ? hideFigureByImageIndex(sanitizeHtml(post.htmlBody), featuredDocIndex) : ''),
    [post, featuredDocIndex]
  );

  // Live preview of whichever image is currently selected as featured — works
  // for the in-progress selection (unlocked) and the already-set one (locked).
  const heroImageUrl = useMemo(() => {
    if (!post) return null;
    if (featuredMediaId) {
      const match = post.images.find((img) => img.mediaId === featuredMediaId);
      if (match) return match.media.url;
    }
    // Selected image isn't part of the Doc-pulled strip (a library pick or a
    // fresh upload) — its url only lives in `featuredPreviewUrl`.
    if (featuredPreviewUrl) return featuredPreviewUrl;
    return post.featuredMedia?.url ?? null;
  }, [post, featuredMediaId, featuredPreviewUrl]);

  const currentSiteLabel = useMemo(() => {
    if (channelId == null) return undefined;
    return wpChannels?.find((c) => c.id === channelId)?.siteUrl.replace(/^https?:\/\//, '');
  }, [wpChannels, channelId]);

  const currentSiteIconUrl = useMemo(() => {
    if (channelId == null) return undefined;
    return wpChannels?.find((c) => c.id === channelId)?.siteIconUrl;
  }, [wpChannels, channelId]);

  // "Pulled from Google Doc" link — prefer the direct `gdocUrl`, fall back
  // to reconstructing the edit URL from `gdocId` alone.
  const gdocHref = useMemo(() => {
    if (!post) return null;
    if (post.gdocUrl) return post.gdocUrl;
    if (post.gdocId) return `https://docs.google.com/document/d/${post.gdocId}/edit`;
    return null;
  }, [post]);

  /**
   * `DatePickerSingle` <-> `scheduledAtLocal` bridge.
   *
   * `scheduledAtLocal` stays the NAIVE "YYYY-MM-DDTHH:mm" wall-clock string
   * (unchanged contract — see `handleSubmit` below and the JSDoc on
   * `isScheduleInPast`). `DatePickerSingle` itself speaks ISO strings: its
   * `combineDateAndTime` builds a real `Date` from the picked local
   * year/month/day/hour/minute (via `setHours` on a browser-local `Date`)
   * and returns `.toISOString()` — i.e. it treats the picked digits as
   * browser-local wall-clock time and emits the UTC-equivalent instant.
   *
   * `new Date("YYYY-MM-DDTHH:mm")` (no offset) is parsed as browser-local
   * time per the ES2015 Date Time String Format, so `naiveToPickerISO`
   * exactly inverts that same local<->UTC conversion, and
   * `pickerISOToNaive` (parse the ISO instant, then read it back with
   * date-fns `format`, which uses local getters) exactly inverts
   * `combineDateAndTime`. Round-tripping through either direction cancels
   * the browser's own timezone out — the wall-clock digits the user sees
   * are preserved byte-for-byte, with no double-shift.
   */
  const naiveToPickerISO = (naive: string): string => {
    if (!naive) return '';
    const parsed = new Date(naive);
    return isNaN(parsed.getTime()) ? '' : parsed.toISOString();
  };

  const pickerISOToNaive = (iso: string): string => {
    if (!iso) return '';
    const parsed = new Date(iso);
    return isNaN(parsed.getTime()) ? '' : format(parsed, "yyyy-MM-dd'T'HH:mm");
  };

  /**
   * Past-time validation for the schedule date/time picker.
   *
   * `scheduledAtLocal` is a NAIVE wall-clock string ("YYYY-MM-DDTHH:mm") the
   * user intends in the app's resolved timezone — NOT necessarily the
   * browser's own OS timezone. `new Date(scheduledAtLocal)` parses it using
   * the browser's local timezone rules, but since `toUTC` (== `fromTimezone`,
   * wraps date-fns-tz's `fromZonedTime`) re-reads that same Date via its
   * LOCAL getters and reinterprets those digits as being in `timezone`, the
   * browser's own zone cancels out — the result is the correct UTC instant
   * for "these wall-clock digits, in the app's timezone" regardless of what
   * timezone the browser happens to be set to. This is the pattern
   * `fromTimezone`'s own JSDoc documents: "use this when user picks a time
   * in their timezone and you need to store it as UTC". We only use it here
   * for a client-side UX guard (disallow picking a past time) — the
   * authoritative resolution still happens server-side via the timezone
   * cascade at publish time.
   */
  const isScheduleInPast = useMemo(() => {
    if (!scheduledAtLocal) return false;
    const utcInstant = toUTC(new Date(scheduledAtLocal));
    return utcInstant ? utcInstant.getTime() < Date.now() : false;
  }, [scheduledAtLocal, toUTC]);

  // Shared by both `FeaturedImagePicker` render sites — keeps `featuredMediaId`
  // and its preview url in lockstep for every selection source (Doc strip,
  // library modal, or a fresh upload).
  const handleFeaturedImageSelect = (mediaId: number, url?: string) => {
    setFeaturedMediaId(mediaId);
    if (url) setFeaturedPreviewUrl(url);
  };

  const canSubmit =
    !!channelId &&
    title.trim().length > 0 &&
    (action !== 'schedule' || (!!scheduledAtLocal && !isScheduleInPast));

  const handleSubmit = async () => {
    if (!channelId || !blogPostId) return;

    // Naive local wall-clock string, no seconds trimming needed —
    // datetime-local inputs already emit "YYYY-MM-DDTHH:mm" which the
    // backend's regex accepts (seconds optional).
    const scheduledAt = action === 'schedule' ? scheduledAtLocal : undefined;

    try {
      const result = await publishMutation.mutateAsync({
        action,
        channelId,
        title: title.trim(),
        excerpt: excerpt.trim() || undefined,
        slug: slug.trim() || undefined,
        featuredMediaId,
        categoryIds,
        tagIds,
        scheduledAt,
      });
      invalidateQuotaUsage();

      if (result.alreadyPublished) {
        toast.info(t('blog_already_published', { defaultValue: 'This blog post was already published.' }));
      } else if (action === 'publish') {
        toast.success(t('blog_publish_success', { defaultValue: 'Published to WordPress!' }));
      } else if (action === 'schedule') {
        toast.success(t('blog_schedule_success', { defaultValue: 'Scheduled on WordPress!' }));
      } else {
        toast.success(t('blog_draft_success', { defaultValue: 'Saved as draft on WordPress!' }));
      }

      if (!result.alreadyPublished && result.docRefreshFailed) {
        toast.warning(t('blog_doc_refresh_fallback_warning', {
          defaultValue: "Published the last-synced content. We couldn't reach the Google Doc to pull the latest - reconnect or reshare the folder to refresh.",
        }));
      }
    } catch (err) {
      // Refresh the usage line; a QUOTA_EXCEEDED refusal is already explained by the global upsell modal
      invalidateQuotaUsage();
      if (!isGlobalGatingHandledError(err)) toast.error(formatApiError(err).message);
    }
  };

  /**
   * Phase 3 — pushes the current form fields to the blog's ALREADY-published
   * WordPress post (`POST /:id/update-wp`) instead of creating a new one.
   * `refreshFromDoc` additionally re-pulls the source Google Doc's latest
   * text/images. On success the local `title`/`excerpt`/etc. state (already
   * what was just submitted) stays as the source of truth for the preview
   * while the query invalidation refreshes `post` (htmlBody, images,
   * updatedAt, ...) in the background.
   */
  const handleUpdateOnWp = async () => {
    if (!blogPostId) return;
    setUpdateErrorMsg(null);
    try {
      const result = await updateOnWpMutation.mutateAsync({
        refreshFromDoc,
        title: title.trim(),
        excerpt: excerpt.trim() || undefined,
        slug: slug.trim() || undefined,
        featuredMediaId,
        categoryIds,
        tagIds,
      });
      toast.success(
        result.refreshedFromDoc
          ? t('blog_update_success_refreshed', { defaultValue: 'Updated on WordPress — content refreshed from the Google Doc.' })
          : t('blog_update_success', { defaultValue: 'Updated on WordPress!' })
      );
      setIsEditingLocked(false);
      setRefreshFromDoc(false);
    } catch (err) {
      if (!isGlobalGatingHandledError(err)) setUpdateErrorMsg(formatApiError(err).message);
    }
  };

  /**
   * Explicit "Refresh status" action — forces a live re-check against the
   * WordPress post (`POST /:id/sync-status`). Opening the detail screen
   * already triggers a throttled 60s server-side lazy re-sync, so this is
   * purely for an on-demand nudge (e.g. right after trashing the post on
   * WordPress itself). Surfaces the 400 ("never published") / 502 ("WP
   * unreachable") errors inline via toast, same convention as the rest of
   * this page.
   */
  const handleSyncStatus = async () => {
    try {
      const result = await syncStatusMutation.mutateAsync();
      const label =
        result.status === 'TRASHED'
          ? result.wpStatus === 'deleted'
            ? t('blog_status_removed', { defaultValue: 'Removed' })
            : t('blog_status_trashed', { defaultValue: 'Trashed' })
          : STATUS_LABEL[result.status];
      toast.success(
        t('blog_sync_status_success', { defaultValue: `Status: ${label}`, status: label })
      );
    } catch (err) {
      if (!isGlobalGatingHandledError(err)) toast.error(formatApiError(err).message);
    }
  };

  if (isLoading) {
    return (
      <div className="py-16 text-center">
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-primary-600" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="py-16 text-center">
        <p className="font-medium text-red-600">{t('blog_not_found', { defaultValue: 'Blog post not found' })}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/blog')}>
          {t('blog_back_to_list', { defaultValue: 'Back to Blog' })}
        </Button>
      </div>
    );
  }

  // ─── The "article" reading pane — reused by both the editing layout and
  //     the locked/published summary layout so the two never drift apart. ──
  const articleCard = (
    <div className="bg-white rounded-xl shadow-card overflow-hidden">
      {heroImageUrl && (
        <div className="w-full aspect-[21/9] sm:aspect-[3/1] bg-gray-100 overflow-hidden">
          <RetryImage src={heroImageUrl} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      <div className={cn('p-6 sm:p-10', !heroImageUrl && 'pt-8 sm:pt-10')}>
        <div className="max-w-[65ch] mx-auto">
          <h2 className="font-wagner font-bold text-gray-900 text-2xl sm:text-3xl leading-tight mb-4 break-words">
            {title.trim() || t('blog_untitled_preview', { defaultValue: 'Untitled blog post' })}
          </h2>
          {excerpt.trim() && (
            <p className="text-lg text-gray-500 italic leading-relaxed mb-8 border-l-2 border-primary-200 pl-4">
              {excerpt}
            </p>
          )}
          <div className={ARTICLE_CLASSES} dangerouslySetInnerHTML={{ __html: sanitizedPreview }} />
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader
        title={post.title}
        subtitle={t('blog_detail_subtitle', { defaultValue: 'Review, confirm, and publish to WordPress' })}
        icon={ICONS.BLOG}
        headerType="list"
        documentTitle={post.title}
        backTo="/blog"
        backLabel={t('blog_page_title', { defaultValue: 'Blog' })}
      />

      <div className="max-w-6xl mx-auto space-y-6">
        {/* ─── Header bar: editable headline + status + view-live link ─── */}
        <div className="space-y-3">
          {isLocked ? (
            <h1 className="text-2xl sm:text-3xl font-wagner font-bold text-gray-900 break-words">{post.title}</h1>
          ) : (
            <input
              id="blog-title-header"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={500}
              placeholder={t('blog_title_placeholder', { defaultValue: 'Untitled blog post' })}
              aria-label={t('blog_title_label', { defaultValue: 'Title' })}
              className="w-full bg-transparent border-0 border-b-2 border-transparent focus:border-primary-300 outline-none text-2xl sm:text-3xl font-wagner font-bold text-gray-900 placeholder-gray-300 pb-1 transition-colors"
            />
          )}
          <div className="flex items-center gap-3 flex-wrap">
            <StatusPill status={post.status} label={STATUS_LABEL[post.status]} />
            {post.status === 'PUBLISHED' && post.publishedAt && (
              <span className="text-sm text-gray-500">
                {t('blog_published_on', { defaultValue: `Published: ${formatDateTime(post.publishedAt)}`, date: formatDateTime(post.publishedAt) })}
              </span>
            )}
            {post.status === 'SCHEDULED' && post.scheduledAt && (
              <span className="text-sm text-gray-500">
                {t('blog_scheduled_for', { defaultValue: `Scheduled: ${formatDateTime(post.scheduledAt)}`, date: formatDateTime(post.scheduledAt) })}
              </span>
            )}
            {post.wpPostUrl && post.status === 'PUBLISHED' && (
              <a
                href={post.wpPostUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                {t('blog_view_on_wordpress', { defaultValue: 'View on WordPress' })}
                <Icon name={ICONS.EXTERNAL_LINK} size="xs" ariaHidden />
              </a>
            )}
            {post.wpPostUrl && post.status !== 'PUBLISHED' && (
              <span className="text-sm text-gray-400 flex items-center gap-1">
                {post.status === 'SCHEDULED'
                  ? t('blog_wp_scheduled_hint', { defaultValue: 'Viewable once it goes live' })
                  : post.status === 'TRASHED'
                    ? t('blog_wp_trashed_hint', { defaultValue: 'Removed from WordPress' })
                    : t('blog_wp_draft_hint', { defaultValue: 'Publish to view on WordPress' })}
              </span>
            )}
            {/* Explicit "Refresh status" nudge — only meaningful once this
                blog has actually been pushed to WordPress (has a wpPostId).
                Loading the detail already lazy-syncs (throttled 60s), so
                this is for an on-demand check (e.g. right after trashing
                the post directly on WordPress). */}
            {(post.wpPostUrl || post.wpPostId) && (
              <div className="ml-auto flex flex-col items-end">
                <button
                  type="button"
                  onClick={handleSyncStatus}
                  disabled={syncStatusMutation.isPending}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-gray-600 disabled:opacity-60 transition-colors"
                >
                  <Icon
                    name={ICONS.REFRESH}
                    size="xs"
                    ariaHidden
                    className={syncStatusMutation.isPending ? 'animate-spin' : ''}
                  />
                  {t('blog_refresh_status_cta', { defaultValue: 'Refresh status' })}
                </button>
                {post.wpStatusCheckedAt && (
                  <span className="text-[11px] text-gray-300 mt-0.5">
                    {t('blog_last_checked', { defaultValue: `checked ${formatRelativeDate(post.wpStatusCheckedAt)}`, date: formatRelativeDate(post.wpStatusCheckedAt) })}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Author / timestamps / image count / source-doc meta — shown for
              both the editing and locked layouts. All items share the same
              visual weight; a muted dot separator is interleaved only
              BETWEEN present items so it never dangles or doubles up. */}
          {(() => {
            const metaItems: React.ReactNode[] = [];
            if (post.author) {
              metaItems.push(
                <span key="author">
                  {t('blog_meta_by', { defaultValue: `by ${post.author.fullName}`, name: post.author.fullName })}
                </span>
              );
            }
            metaItems.push(
              <span key="created">
                {t('blog_meta_created', {
                  defaultValue: `Created ${formatRelativeDate(post.createdAt)}`,
                  date: formatRelativeDate(post.createdAt),
                })}
              </span>
            );
            metaItems.push(
              <span key="updated">
                {t('blog_meta_updated', {
                  defaultValue: `Updated ${formatRelativeDate(post.updatedAt)}`,
                  date: formatRelativeDate(post.updatedAt),
                })}
              </span>
            );
            if (post.imageCount > 0) {
              metaItems.push(
                <span key="images" className="inline-flex items-center gap-1">
                  <Icon name={ICONS.IMAGE} size="xs" ariaHidden />
                  {t('blog_meta_image_count', { defaultValue: `${post.imageCount} images`, count: post.imageCount })}
                </span>
              );
            }
            if (gdocHref) {
              metaItems.push(
                <a
                  key="gdoc"
                  href={gdocHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-gray-400 hover:text-primary-600 transition-colors"
                >
                  {t('blog_pulled_from_gdoc', { defaultValue: 'Pulled from Google Doc' })}
                  <Icon name={ICONS.EXTERNAL_LINK} size="xs" ariaHidden />
                </a>
              );
            }

            return (
              <div className="flex items-center gap-x-3 gap-y-1.5 flex-wrap text-xs text-gray-400">
                {metaItems.map((item, index) => (
                  <Fragment key={index}>
                    {index > 0 && (
                      <span className="text-gray-300" aria-hidden>
                        ·
                      </span>
                    )}
                    {item}
                  </Fragment>
                ))}
              </div>
            );
          })()}
        </div>

        {post.status === 'FAILED' && post.lastError && (
          <div className="flex flex-col sm:flex-row sm:items-start gap-3 rounded-xl bg-red-50 border border-red-200 p-4">
            <div className="flex items-start gap-2 flex-1 min-w-0">
              <Icon name={ICONS.WARNING} size="sm" color="text-red-600" ariaHidden className="mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-red-800">
                  {t('blog_failed_title', { defaultValue: 'Last publish attempt failed' })}
                </p>
                <p className="text-xs text-red-700 mt-0.5 break-words">{post.lastError}</p>
                {post.retryCount > 0 && (
                  <p className="text-xs text-red-600 mt-1">
                    {t('blog_failed_retry_count', {
                      defaultValue: `Already retried ${post.retryCount} time${post.retryCount === 1 ? '' : 's'}`,
                      count: post.retryCount,
                    })}
                  </p>
                )}
                <p className="text-xs text-red-600 mt-1">
                  {t('blog_failed_retry_hint', { defaultValue: 'Fix the fields below if needed — retrying is safe, it will not create a duplicate post.' })}
                </p>
              </div>
            </div>
            <Button
              variant="danger"
              size="sm"
              onClick={handleSubmit}
              disabled={!canSubmit || publishMutation.isPending}
              loading={publishMutation.isPending}
              className="flex-shrink-0"
            >
              <Icon name={ICONS.REFRESH} size="sm" className="mr-1.5" ariaHidden />
              {t('blog_retry_publish', { defaultValue: 'Retry publish' })}
            </Button>
          </div>
        )}

        {post.status === 'TRASHED' && (
          <div className="flex flex-col sm:flex-row sm:items-start gap-3 rounded-xl bg-gray-50 border border-gray-200 p-4">
            <div className="flex items-start gap-2 flex-1 min-w-0">
              <Icon name="recycle-bin" size="sm" color="text-gray-400" ariaHidden className="mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-700">
                  {post.wpStatus === 'deleted'
                    ? t('blog_trashed_title_deleted', { defaultValue: 'This post was removed from WordPress' })
                    : t('blog_trashed_title', { defaultValue: 'This post was trashed on WordPress' })}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {t('blog_trashed_description', {
                    defaultValue: "It's no longer live at its published URL. Publishing again puts it back on WordPress without creating a duplicate.",
                  })}
                </p>
                {post.wpPostUrl && (
                  <a
                    href={post.wpPostUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 mt-1.5"
                  >
                    {t('blog_trashed_dead_link', { defaultValue: 'Last known URL (may be dead)' })}
                    <Icon name={ICONS.EXTERNAL_LINK} size="xs" ariaHidden />
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {isLocked ? (
          /* ─── Read-only "done!" summary — no update-existing support in v1 (decision B) ─── */
          <>
            <div className="bg-white rounded-xl shadow-card p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                {heroImageUrl && (
                  <div className="w-full sm:w-32 h-32 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                    <RetryImage src={heroImageUrl} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1 min-w-0 space-y-2.5">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <Icon name={ICONS.CHECK_CIRCLE} size="sm" color="text-green-600" ariaHidden />
                      </div>
                      <h2 className="text-base font-semibold text-gray-900">
                        {post.status === 'SCHEDULED'
                          ? t('blog_locked_scheduled_title', { defaultValue: 'Scheduled to go live' })
                          : t('blog_locked_published_title', { defaultValue: "You're live on WordPress" })}
                      </h2>
                    </div>
                    {!isEditingLocked && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditingLocked(true)}
                        className="flex-shrink-0"
                      >
                        <Icon name={ICONS.EDIT} size="xs" className="mr-1.5" ariaHidden />
                        {t('blog_edit_update_cta', { defaultValue: 'Edit & Update' })}
                      </Button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {currentSiteLabel && (
                      <MetaBadge leading={<SiteFavicon iconUrl={currentSiteIconUrl} siteName={currentSiteLabel} size="xs" />}>
                        {currentSiteLabel}
                      </MetaBadge>
                    )}
                    {post.status === 'SCHEDULED' && post.scheduledAt && (
                      <MetaBadge icon={ICONS.CLOCK}>{formatDateTime(post.scheduledAt)}</MetaBadge>
                    )}
                    {post.status === 'PUBLISHED' && post.publishedAt && (
                      <MetaBadge icon={ICONS.CHECK_CIRCLE}>{formatDateTime(post.publishedAt)}</MetaBadge>
                    )}
                  </div>
                  <TermCluster
                    label={t('blog_locked_categories_label', { defaultValue: 'Categories:' })}
                    icon={ICONS.FOLDER}
                    ids={post.categoryIds}
                    terms={categories}
                    isLoading={isLoadingCategories}
                  />
                  <TermCluster
                    label={t('blog_locked_tags_label', { defaultValue: 'Tags:' })}
                    icon={ICONS.TAG}
                    ids={post.tagIds}
                    terms={tags}
                    isLoading={isLoadingTags}
                    variant="mint"
                  />
                  <div className="flex flex-wrap items-center gap-3 pt-1">
                    {post.wpPostUrl && (
                      <a
                        href={post.wpPostUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700"
                      >
                        {t('blog_view_live_post', { defaultValue: 'Visit the live post' })}
                        <Icon name={ICONS.EXTERNAL_LINK} size="xs" ariaHidden />
                      </a>
                    )}
                    {post.socialDraftsCount > 0 && (
                      <button
                        type="button"
                        onClick={() => navigate('/calendar')}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-soft-mint text-dark-teal text-xs font-semibold rounded-full hover:bg-soft-mint/70 transition-colors"
                      >
                        <Icon name={ICONS.MAGIC_WAND} size="xs" ariaHidden />
                        {t('blog_social_drafts_chip', {
                          defaultValue: `${post.socialDraftsCount} social draft${post.socialDraftsCount === 1 ? '' : 's'} generated from this blog`,
                          count: post.socialDraftsCount,
                        })}
                        <Icon name={ICONS.EXTERNAL_LINK} size="xs" ariaHidden />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {isEditingLocked && channelId && (
                <div className="mt-6 pt-6 border-t border-gray-100 space-y-5">
                  <div>
                    <label htmlFor="blog-update-title" className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                      {t('blog_title_label', { defaultValue: 'Title' })}
                    </label>
                    <Input
                      id="blog-update-title"
                      type="text"
                      inputSize="sm"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      maxLength={500}
                    />
                  </div>

                  <FeaturedImagePicker
                    images={post.images}
                    selectedMediaId={featuredMediaId}
                    selectedPreviewUrl={featuredPreviewUrl}
                    onSelect={handleFeaturedImageSelect}
                  />

                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                      {t('blog_site_label', { defaultValue: 'WordPress Site' })}
                    </label>
                    <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg">
                      <SiteFavicon iconUrl={currentSiteIconUrl} siteName={currentSiteLabel} size="sm" />
                      {currentSiteLabel}
                      <span className="text-xs text-gray-400">
                        {t('blog_site_fixed_hint', { defaultValue: '(fixed — already published here)' })}
                      </span>
                    </div>
                  </div>

                  <CategoryTagFields
                    channelId={channelId}
                    categories={categories}
                    tags={tags}
                    isLoadingCategories={isLoadingCategories}
                    isLoadingTags={isLoadingTags}
                    categoryIds={categoryIds}
                    tagIds={tagIds}
                    onCategoryIdsChange={setCategoryIds}
                    onTagIdsChange={setTagIds}
                  />

                  <div>
                    <label htmlFor="blog-update-slug" className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                      {t('blog_slug_label', { defaultValue: 'Slug (optional)' })}
                    </label>
                    <Input
                      id="blog-update-slug"
                      type="text"
                      inputSize="sm"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      maxLength={200}
                    />
                  </div>

                  <div>
                    <label htmlFor="blog-update-excerpt" className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                      {t('blog_excerpt_label', { defaultValue: 'Excerpt (optional)' })}
                    </label>
                    <Textarea
                      id="blog-update-excerpt"
                      value={excerpt}
                      onChange={(e) => setExcerpt(e.target.value)}
                      rows={4}
                      className="!resize-y min-h-[96px]"
                      maxLength={5000}
                    />
                  </div>

                  <label className="flex items-start gap-2.5 rounded-lg border border-gray-200 bg-gray-50 p-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={refreshFromDoc}
                      onChange={(e) => setRefreshFromDoc(e.target.checked)}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="block text-sm font-medium text-gray-800">
                        {t('blog_refresh_from_doc_label', { defaultValue: 'Refresh content from Google Doc' })}
                      </span>
                      <span className="block text-xs text-gray-500 mt-0.5">
                        {t('blog_refresh_from_doc_hint', {
                          defaultValue: 'Pull the latest text & images from the source Doc into this post.',
                        })}
                      </span>
                    </span>
                  </label>

                  {updateErrorMsg && (
                    <p className="text-xs text-red-600" role="alert">
                      {updateErrorMsg}
                    </p>
                  )}

                  <div className="flex items-center gap-3">
                    <Button
                      variant="primary"
                      onClick={handleUpdateOnWp}
                      disabled={!title.trim() || updateOnWpMutation.isPending}
                      loading={updateOnWpMutation.isPending}
                    >
                      <Icon name={ICONS.REFRESH} size="sm" className="mr-1.5" ariaHidden />
                      {t('blog_update_submit', { defaultValue: 'Update on WordPress' })}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsEditingLocked(false);
                        setRefreshFromDoc(false);
                        setUpdateErrorMsg(null);
                      }}
                      disabled={updateOnWpMutation.isPending}
                    >
                      {t('blog_update_cancel', { defaultValue: 'Cancel' })}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {articleCard}

            {/* Phase 2 (SPEC.md decision C) — prompt to repurpose this blog into social drafts. */}
            <div className="rounded-xl border border-primary-200 bg-gradient-to-br from-primary-50 via-white to-soft-mint/30 shadow-card p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <Icon name={ICONS.MAGIC_WAND} size="md" color="text-primary-600" ariaHidden />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t('blog_generate_social_title', { defaultValue: 'Generate social posts from this blog?' })}
                  </h3>
                  <p className="text-xs text-gray-600 mt-0.5">
                    {t('blog_generate_social_subtitle', {
                      defaultValue: 'AI turns this post into ready-to-edit LinkedIn/Instagram drafts using your brand voice — nothing is scheduled automatically.',
                    })}
                  </p>
                </div>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setGenerateSocialModalOpen(true)}
                className="flex-shrink-0"
              >
                <Icon name={ICONS.SPARKLES} size="sm" className="mr-1.5" ariaHidden />
                {t('blog_generate_social_cta', { defaultValue: 'Generate Social Posts' })}
              </Button>
            </div>
          </>
        ) : (
          /* ─── Editorial two-column preview + publish layout ─── */
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
            {/* LEFT — the article preview */}
            <div className="min-w-0">{articleCard}</div>

            {/* RIGHT — sticky publish panel */}
            <div className="lg:sticky lg:top-6 space-y-4">
              {(!wpChannels || wpChannels.length === 0) && !isLoadingChannels && (
                <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <Icon name={ICONS.WARNING} size="md" color="text-amber-600" ariaHidden className="mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">
                      {t('blog_no_wp_site_title', { defaultValue: 'Connect a WordPress site first' })}
                    </p>
                    <p className="mt-1 text-sm text-amber-700">
                      {t('blog_no_wp_site_description', { defaultValue: 'Connect a self-hosted WordPress site in Settings > Channels before publishing.' })}
                    </p>
                    <Button variant="primary" size="sm" className="mt-3" onClick={() => navigate('/settings?tab=channels')}>
                      {t('blog_connect_wp_action', { defaultValue: 'Go to Settings' })}
                    </Button>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-xl shadow-card p-5 sm:p-6 space-y-5">
                <div className="flex items-center gap-2">
                  <Icon name={ICONS.ROCKET} size="sm" color="text-primary-600" ariaHidden />
                  <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {t('blog_publish_panel_title', { defaultValue: 'Publish' })}
                  </h2>
                </div>

                {/* Featured image */}
                <FeaturedImagePicker
                    images={post.images}
                    selectedMediaId={featuredMediaId}
                    selectedPreviewUrl={featuredPreviewUrl}
                    onSelect={handleFeaturedImageSelect}
                  />

                {/* WordPress site */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                    {t('blog_site_label', { defaultValue: 'WordPress Site' })}
                  </label>
                  <Select
                    value={channelId?.toString() ?? ''}
                    onValueChange={handleChannelChange}
                    placeholder={
                      isLoadingChannels
                        ? t('blog_sites_loading', { defaultValue: 'Loading sites...' })
                        : t('blog_site_placeholder', { defaultValue: 'Choose a site...' })
                    }
                    options={
                      wpChannels?.map((c) => ({ value: c.id.toString(), label: c.siteUrl.replace(/^https?:\/\//, '') })) ?? []
                    }
                    disabled={isLoadingChannels}
                  />
                  {channelId != null && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
                      <SiteFavicon iconUrl={currentSiteIconUrl} siteName={currentSiteLabel} size="xs" />
                      <span className="truncate">{currentSiteLabel}</span>
                    </div>
                  )}
                </div>

                {/* Categories & tags */}
                {channelId && (
                  <CategoryTagFields
                    channelId={channelId}
                    categories={categories}
                    tags={tags}
                    isLoadingCategories={isLoadingCategories}
                    isLoadingTags={isLoadingTags}
                    categoryIds={categoryIds}
                    tagIds={tagIds}
                    onCategoryIdsChange={setCategoryIds}
                    onTagIdsChange={setTagIds}
                  />
                )}

                {/* Excerpt + slug */}
                <div className="space-y-4 pt-1 border-t border-gray-100">
                  <div>
                    <label htmlFor="blog-slug" className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                      {t('blog_slug_label', { defaultValue: 'Slug (optional)' })}
                    </label>
                    <Input
                      id="blog-slug"
                      type="text"
                      inputSize="sm"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      placeholder={t('blog_slug_placeholder', { defaultValue: 'auto-generated-by-wordpress' })}
                      maxLength={200}
                    />
                  </div>
                  <div>
                    <label htmlFor="blog-excerpt" className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                      {t('blog_excerpt_label', { defaultValue: 'Excerpt (optional)' })}
                    </label>
                    <Textarea
                      id="blog-excerpt"
                      value={excerpt}
                      onChange={(e) => setExcerpt(e.target.value)}
                      rows={4}
                      className="!resize-y min-h-[96px]"
                      maxLength={5000}
                    />
                  </div>
                </div>

                {/* Action + schedule + submit */}
                <div className="space-y-3 pt-1 border-t border-gray-100">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {t('blog_action_label', { defaultValue: 'Action' })}
                  </label>
                  <div className="grid grid-cols-3 gap-1 p-1 bg-gray-100 rounded-lg">
                    {ACTION_SEGMENTS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setAction(opt.value)}
                        className={cn(
                          'flex items-center justify-center gap-1 py-2 rounded-md text-xs font-medium transition-all',
                          action === opt.value
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        )}
                      >
                        <Icon name={opt.icon} size="xs" ariaHidden />
                        <span className="truncate">{ACTION_LABEL[opt.value]}</span>
                      </button>
                    ))}
                  </div>

                  {action === 'schedule' && (
                    <div>
                      <DatePickerSingle
                        label={t('blog_schedule_at_label', { defaultValue: 'Schedule for' })}
                        value={naiveToPickerISO(scheduledAtLocal)}
                        onChange={(isoDate) => {
                          setScheduledAtLocal(pickerISOToNaive(isoDate));
                          setScheduleTouched(true);
                        }}
                        showTime
                        minDate={new Date()}
                        placeholder="dd.mm.yyyy HH:mm"
                        className="w-full"
                      />
                      {scheduleTouched && isScheduleInPast && (
                        <p className="mt-1 text-xs text-red-600">
                          {t('blog_schedule_past_error', { defaultValue: 'Please pick a time in the future.' })}
                        </p>
                      )}
                    </div>
                  )}

                  <BlogQuotaUsageLine />

                  <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={!canSubmit || publishMutation.isPending}
                    loading={publishMutation.isPending}
                    className="w-full"
                  >
                    {action === 'publish' && (
                      <Icon name={ICONS.ROCKET} size="sm" className="mr-1.5" ariaHidden />
                    )}
                    {action === 'draft' && (
                      <Icon name={ICONS.DRAFT} size="sm" className="mr-1.5" ariaHidden />
                    )}
                    {action === 'schedule' && (
                      <Icon name={ICONS.CLOCK} size="sm" className="mr-1.5" ariaHidden />
                    )}
                    {action === 'publish' && t('blog_submit_publish', { defaultValue: 'Publish to WordPress' })}
                    {action === 'draft' && t('blog_submit_draft', { defaultValue: 'Save as Draft on WordPress' })}
                    {action === 'schedule' && t('blog_submit_schedule', { defaultValue: 'Schedule on WordPress' })}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {isLocked && blogPostId && (
        <BlogGenerateSocialModal
          open={generateSocialModalOpen}
          onOpenChange={setGenerateSocialModalOpen}
          blogPostId={blogPostId}
        />
      )}
    </div>
  );
};

export default BlogDetail;
